import { useState, useCallback } from 'react';
import useAuth from 'src/hooks/useAuth';
import { useDispatch } from 'src/redux/store';
import { getListaColunas, getListaTickets } from 'src/redux/slices/chamados';
import actionCriarChamado from 'src/actions/chamados/actionCriarChamado';
import v2_actionUploadS3 from 'src/actions/v2/global/actionUploadS3';
import v2_actionBuscarDadosCliente from 'src/actions/v2/cliente/v2_actionBuscarDadosCliente';
import actionListaInputsPrioridade from 'src/actions/chamados/actionListaInputsPrioridade';
import { actionListaPrioridades } from 'src/actions/chamados/actionChamadosConfig';

export default function useNovoChamado() {
  const dispatch = useDispatch();
  const { user } = useAuth();

  const [isOpen, setIsOpen] = useState(false);
  const [assunto, setAssunto] = useState('');
  const [descricao, setDescricao] = useState('');
  const [prioridade, setPrioridade] = useState('');
  const [arquivos, setArquivos] = useState([]);
  const [direcionadoPara, setDirecionadoPara] = useState('');
  const [direcionadoSetores, setDirecionadoSetores] = useState('');
  const [tipoLocal, setTipoLocal] = useState('chamado_interno');
  const [setorOuUsuario, setSetorOuUsuario] = useState('');
  const [setorEscolhido, setSetorEscolhido] = useState('');
  const [usuarioEscolhido, setUsuarioEscolhido] = useState('');
  const [filialEscolhido, setFilialEscolhido] = useState('');
  const [estimativa, setEstimativa] = useState('');
  const [files, setFiles] = useState([]);
  const [preview, setPreview] = useState(true);
  const [isFile, setIsFile] = useState(false);
  const [modalAssunto, setModalAssunto] = useState(false);
  const [emCopia, setEmCopia] = useState([]);
  const [dataConclusao, setDataConclusao] = useState('');
  const [config, setConfig] = useState(null);
  const [listaInputsPrioridade, setListaInputsPrioridade] = useState([]);
  const [loading, setLoading] = useState(false);
  const [chatId, setChatId] = useState(null);
  const [onSuccessCallback, setOnSuccessCallback] = useState(null);
  const [caixaEntrada, setCaixaEntrada] = useState('');
  const [respondenteName, setRespondenteName] = useState('');
  const [respondenteEmail, setRespondenteEmail] = useState('');
  const [checklist, setChecklist] = useState([]);

  const filesUrl = [];

  function limparCampos() {
    setAssunto('');
    setDescricao('');
    setPrioridade('');
    setArquivos([]);
    setDirecionadoPara('');
    setDirecionadoSetores('');
    setSetorOuUsuario('');
    setSetorEscolhido('');
    setUsuarioEscolhido('');
    setFilialEscolhido('');
    setEmCopia([]);
    setEstimativa('');
    setChatId(null);
    setCaixaEntrada('');
    setRespondenteName('');
    setRespondenteEmail('');
    setChecklist([]);
  }

  async function uploadFilesAndGetUrl(filesToUpload) {
    if (filesToUpload.length > 0) {
      for (let file of filesToUpload) {
        var formData = new FormData();
        formData.append('file', file);
        try {
          let querys = `?path=chamados/clientes/${user.clienteId}/anexos`;
          const response = await v2_actionUploadS3(formData, querys);
          if (!response.data.error) {
            filesUrl.push({ key: response.data.data.key, type: file.type, name: file.name, size: file.size });
          }
        } catch (error) {
          console.log('erro ao upload arquivo', error);
        }
      }
    }
  }

  const onCriarChamado = async () => {
    setLoading(true);
    let data = {
      assunto,
      descricao,
      criadoPor: user.id,
      copiadoPara: emCopia.length > 0 ? emCopia.map(item => item._id) : [],
      arquivos: filesUrl,
      direcionadoPara: setorOuUsuario === 'usuario' ? usuarioEscolhido._id : null,
      direcionadoFilial: filialEscolhido ? filialEscolhido.codigo_filial : null,
      direcionadoSetores: setorEscolhido ? setorEscolhido._id : null,
      dataConclusao,
      input_prioridade: prioridade || null,
      tipo: tipoLocal === 'chamado_externo' ? 'externo' : 'interno',
      chat_id: chatId || null,
      caixa_entrada_id: caixaEntrada ? caixaEntrada._id : null,
      respondente: tipoLocal === 'chamado_externo' ? { nome: respondenteName, email: respondenteEmail } : null,
      checklist: checklist.length > 0 ? checklist : [],
    };
    try {
      if (files.length > 0) await uploadFilesAndGetUrl(files);
      await actionCriarChamado(user.clienteId, data);
      limparCampos();
      setIsOpen(false);
      dispatch(getListaTickets({ setorId: user.setor_id, usuarioId: user.id, filial: user.filial }));
      dispatch(getListaColunas({ setorId: user.setor_id, usuarioId: user.id, filial: user.filial }));
      if (onSuccessCallback) onSuccessCallback();
      setLoading(false);
    } catch (error) {
      console.log('error', error);
      setLoading(false);
    }
  };

  const handleDropMultiFile = useCallback(
    (acceptedFiles) => {
      setFiles(acceptedFiles.map((file) => Object.assign(file, { preview: URL.createObjectURL(file) })));
    },
    [setFiles]
  );

  const handleRemove = (file) => {
    const filteredItems = files.filter((_file) => _file !== file);
    setFiles(filteredItems);
  };

  async function buscarPrioridadesPorCaixa(caixaObj) {
    try {
      const response = await actionListaPrioridades(`?cliente_id=${user.clienteId}&caixa_entrada_id=${caixaObj._id}`);
      setListaInputsPrioridade(response.data.data);
    } catch (error) {
      console.log('error ao buscar prioridades por caixa', error);
    }
  }

  async function buscarConfiguracoes() {
    try {
      const response = await v2_actionBuscarDadosCliente(user.clienteId);
      const response_inputs = await actionListaInputsPrioridade(user.clienteId);
      setConfig(response.data.data);
      setListaInputsPrioridade(response_inputs.data.data);
    } catch (error) {
      console.log('error', error);
    }
  }

  const handleOpenNovoChamado = async () => {
    await buscarConfiguracoes();
    setIsOpen(true);
  };

  const handleOpenNovoChamadoComChat = async (chatIdParam, successCallback) => {
    setChatId(chatIdParam);
    if (successCallback) setOnSuccessCallback(() => successCallback);
    await buscarConfiguracoes();
    setIsOpen(true);
  };

  const selectAssunto = (item) => {
    setAssunto(item.assunto);
    setEstimativa(item.prioridade);
    setTipoLocal(item.loja_ou_filial);
    setSetorOuUsuario(item.setor_ou_usuario);
    if (item.loja_ou_filial === 'loja') {
      setFilialEscolhido(item.filial);
    }
    if (item.loja_ou_filial === 'backoffice') {
      if (item.setor_ou_usuario === 'setor') {
        setSetorEscolhido(item.setor);
      } else if (item.setor_ou_usuario === 'usuario') {
        setUsuarioEscolhido(item.usuario);
      }
    }
    setIsFile(item.isFile);
    setDescricao(item.descricao);
    setModalAssunto(false);
  };

  const novoChamadoProps = {
    loading,
    user,
    config: config && config.config,
    listaInputsPrioridade,
    tipoLocal,
    setorOuUsuario,
    usuarioEscolhido,
    setorEscolhido,
    filialEscolhido,
    estimativa,
    selectAssunto: (list) => selectAssunto(list),
    changeEmCopia: (newValue) => setEmCopia(newValue),
    changeEstimativa: (e) => setEstimativa(e.target.value),
    changeTipoLocal: (e) => {
      const newTipo = e.target.value;
      setTipoLocal(newTipo);
      if (newTipo === 'loja') {
        setUsuarioEscolhido('');
        setSetorEscolhido('');
        setSetorOuUsuario('');
      } else if (newTipo === 'backoffice') {
        setFilialEscolhido('');
      }
      if (newTipo === 'chamado_externo') {
        setListaInputsPrioridade([]);
        setPrioridade('');
      } else if (newTipo === 'chamado_interno') {
        setPrioridade('');
        actionListaInputsPrioridade(user.clienteId).then(res => {
          setListaInputsPrioridade(res.data.data);
        }).catch(() => {});
      }
    },
    changeSetorOuUsuario: (e) => {
      setSetorOuUsuario(e.target.value);
      if (e.target.value === 'setor') {
        setUsuarioEscolhido('');
      } else if (e.target.value === 'usuario') {
        setSetorEscolhido('');
      }
    },
    changeUsuarioEscolhido: (newValue) => setUsuarioEscolhido(newValue),
    changeFilialEscolhido: (newValue) => setFilialEscolhido(newValue),
    changeSetorEscolhido: (newValue) => setSetorEscolhido(newValue),
    changeIsFile: (e) => setIsFile(e.target.checked),
    modalAssunto,
    closeModalAssunto: () => setModalAssunto(false),
    openModalAssunto: () => setModalAssunto(true),
    emCopia,
    isFile,
    preview,
    files,
    assunto,
    descricao,
    prioridade,
    arquivos,
    direcionadoPara,
    direcionadoSetores,
    changePrioridade: (value) => setPrioridade(value),
    changeAssunto: (e) => setAssunto(e.target.value),
    changeDescricao: (value) => setDescricao(value),
    changeDirecionadoPara: (e) => setDirecionadoPara(e.target.value),
    changeDirecionadoSetores: (e) => setDirecionadoSetores(e.target.value),
    isOpen,
    onClose: () => {
      limparCampos();
      setIsOpen(false);
    },
    handleDropMultiFile,
    handleRemove,
    onCriarChamado,
    dataConclusao,
    changeDataConclusao: (e) => setDataConclusao(e),
    caixaEntrada,
    changeCaixaEntrada: (newValue) => {
      setCaixaEntrada(newValue);
      setPrioridade('');
      if (newValue) {
        buscarPrioridadesPorCaixa(newValue);
      } else {
        setListaInputsPrioridade([]);
      }
    },
    respondenteName,
    changeRespondenteName: (e) => setRespondenteName(e.target.value),
    respondenteEmail,
    changeRespondenteEmail: (e) => setRespondenteEmail(e.target.value),
    checklist,
    addChecklistItem: (text) => {
      if (!text.trim()) return;
      setChecklist(prev => [...prev, { id: Date.now().toString(), text: text.trim(), checked: false }]);
    },
    toggleChecklistItem: (itemId) => {
      setChecklist(prev => prev.map(item => item.id === itemId ? { ...item, checked: !item.checked } : item));
    },
    removeChecklistItem: (itemId) => {
      setChecklist(prev => prev.filter(item => item.id !== itemId));
    },
  };

  return {
    novoChamadoProps,
    handleOpenNovoChamado,
    handleOpenNovoChamadoComChat,
    isOpen,
  };
}
